import React, { useEffect, useState } from 'react';
import notify from 'lib/notify';
import { sendApiRequest } from 'lib/api/public';
import { MenuItem, Select } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import StatusForm from 'components/settings/StatusForm';
import { accentActionSx } from 'components/common/utils';
import { getTourSelectMenuProps } from 'components/onboarding/tourMenuStacking';
import { FieldError } from 'react-hook-form';

type StatusOption = {
  _id: string;
  label: string;
};

type Props = {
  defaultValue?: string;
  endpoint: string;
  onSelect: (statusId: string) => void;
  error?: FieldError;
  testId?: string;
  /** When set, raises dropdown z-index only while this onboarding form tour is active. */
  tourMenuTaskId?: string;
};

const StatusSelector = ({ defaultValue, onSelect, endpoint, error, testId, tourMenuTaskId }: Props) => {
  const [status, setStatus] = useState(defaultValue);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [showForm, setShowForm] = useState(false);

  const fetchStatuses = async () => {
    try {
      const response = await sendApiRequest<{ list: StatusOption[] }>({
        path: endpoint,
        method: 'GET',
      });

      setStatuses(response.list);
    } catch (e) {
      notify(e);
      setStatuses([]);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;

    if (value === 'add_new') {
      setShowForm(true);
    } else {
      setStatus(value);
      onSelect(value);
    }
  };

  const renderFormDialog = () => {
    if (!showForm) {
      return null;
    }

    return (
      <StatusForm
        endpoint={endpoint}
        open={showForm}
        onClose={() => setShowForm(false)}
        onSuccess={response => {
          fetchStatuses().then(() => {
            if (!response) {
              return;
            }

            setStatus(response._id);
            onSelect(response._id);
            setShowForm(false);
          });
        }}
      />
    );
  };

  return (
    <>
      <Select
        value={statuses.length > 0 ? status || '' : ''}
        sx={{ width: '100%' }}
        onChange={handleStatusChange}
        error={!!error}
        displayEmpty={!status}
        renderValue={selected => {
          if (!selected) return '';
          const selectedStatus = statuses.find(s => s._id === selected);
          return selectedStatus ? selectedStatus.label : '';
        }}
        size="small"
        data-testid={testId}
        MenuProps={getTourSelectMenuProps(tourMenuTaskId)}
      >
        <MenuItem value="add_new" sx={accentActionSx}>
          + Add new Status
        </MenuItem>
        {statuses.map(status => (
          <MenuItem key={status._id} value={status._id} data-testid={`status-option-${status.label}`}>
            {status.label}
          </MenuItem>
        ))}
      </Select>

      {renderFormDialog()}
    </>
  );
};

export default StatusSelector;
